import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Calendar, Users, Syringe, CheckCircle2, FileText, History,
  Settings, UserCheck, Pill, LogOut, Inbox, FlaskConical, Scale,
  Building2, Check, ChevronsUpDown, Layers, Lock, ShieldCheck, HeartHandshake,
} from 'lucide-react';
import { useCurrentUser, useLogout, useLockCounter } from '@/api/auth';
import { useCounterIdleLock } from '@/lib/useCounterSession';
import { useSites, useSwitchSite } from '@/api/resources';
import { estEquipe, usePlateforme } from '@/api/platform';
import PlatformBanner from './PlatformBanner';

const items = [
  { to: '/dashboard', label: 'Tableau de bord', icon: Home },
  { to: '/planning', label: 'Planning semaine', icon: Calendar },
  { to: '/organization', label: 'Organisation du jour', icon: UserCheck },
  { to: '/appointments', label: 'Rendez-vous métier', icon: Syringe },
  { to: '/employees', label: 'Salariés', icon: Users },
  // Route inchangée (/simulation) — seul le libellé parle le langage du métier.
  { to: '/simulation', label: 'Changer les horaires', icon: FlaskConical },
  { to: '/requests', label: 'Demandes', icon: Inbox },
  { to: '/validation', label: 'Validation semaine', icon: CheckCircle2 },
  { to: '/hours-balance', label: 'Balance des heures', icon: Scale },
  { to: '/shuttle', label: 'Fiche navette', icon: FileText },
  { to: '/shuttle/history', label: 'Historique navette', icon: History },
  { to: '/settings', label: 'Paramètres', icon: Settings },
];

/**
 * Sélecteur de pharmacie (multi-site — M9-3, lot 3A).
 *
 * Ne s'affiche qu'à partir de deux officines : tant qu'il n'y en a qu'une, il
 * n'y a rien à choisir et la barre latérale reste telle qu'elle était.
 * Basculer réémet le jeton côté serveur et vide le cache : chaque écran se
 * recharge pour l'officine choisie.
 */
function SiteSwitcher() {
  const { data } = useSites();
  const switchSite = useSwitchSite();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!data?.multiSite) return null;

  const onMultiSite = pathname.startsWith('/multi-site');
  const active = data.sites.find((s) => s.id === data.activeSiteId);

  const choose = (id: string) => {
    setOpen(false);
    if (onMultiSite) navigate('/planning'); // on quitte la vue d'ensemble
    if (id === data.activeSiteId) return;
    switchSite.mutate(id);
  };

  return (
    <div className="px-2.5 pt-3 border-t border-white/10 relative">
      {open && (
        <div className="absolute bottom-full left-2.5 right-2.5 mb-1 rounded-lg bg-[#16294a] border border-white/15 shadow-xl overflow-hidden z-20">
          {data.sites.map((s) => (
            <button
              key={s.id}
              onClick={() => choose(s.id)}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-left text-[12.5px] text-white/80 hover:bg-white/10"
            >
              <Check size={13} className={!onMultiSite && s.id === data.activeSiteId ? 'opacity-100' : 'opacity-0'} />
              <span className="truncate">{s.name}</span>
            </button>
          ))}
          {/* Toutes les officines l'une sous l'autre — lot 3C. */}
          <button
            onClick={() => { setOpen(false); navigate('/multi-site'); }}
            className="w-full flex items-center gap-2 px-2.5 py-2 text-left text-[12.5px] text-white/80 hover:bg-white/10 border-t border-white/10"
          >
            <Check size={13} className={onMultiSite ? 'opacity-100' : 'opacity-0'} />
            <Layers size={13} className="flex-shrink-0 text-white/50" />
            <span className="truncate">Gestion multi-site</span>
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={switchSite.isPending}
        title="Changer de pharmacie"
        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-left disabled:opacity-60"
      >
        {onMultiSite
          ? <Layers size={15} strokeWidth={1.75} className="text-white/60 flex-shrink-0" />
          : <Building2 size={15} strokeWidth={1.75} className="text-white/60 flex-shrink-0" />}
        <span className="flex-1 min-w-0">
          <span className="block text-[9px] uppercase tracking-[0.12em] text-white/40">
            {onMultiSite ? 'Vue d\'ensemble' : 'Pharmacie'}
          </span>
          <span className="block text-[12.5px] text-white truncate">
            {switchSite.isPending ? 'Basculement…' : onMultiSite ? 'Toutes les officines' : active?.name ?? '—'}
          </span>
        </span>
        <ChevronsUpDown size={13} className="text-white/40 flex-shrink-0" />
      </button>
    </div>
  );
}

/**
 * « Gestion Sociale » — option PAYANTE, affichée seulement si l'équipe
 * PharmaGestion l'a activée pour l'officine. Elle doit sauter aux yeux : dégradé
 * vif, halo, reflet qui passe, badge. Tout le reste de la barre est sobre à dessein.
 */
function LienGestionSociale() {
  return (
    <div className="px-2.5 pt-4">
      <NavLink
        to="/gestion-sociale"
        className={({ isActive }) =>
          `lien-social group relative flex items-center gap-2.5 overflow-hidden rounded-xl px-2.5 py-2.5
           bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-[13px] font-semibold text-white
           shadow-[0_6px_20px_-4px_rgba(45,212,191,0.55)] transition-all duration-200
           hover:-translate-y-px hover:shadow-[0_10px_26px_-4px_rgba(45,212,191,0.75)]
           ${isActive ? 'ring-2 ring-white/80 ring-offset-2 ring-offset-[#0f1f35]' : 'ring-1 ring-white/25'}`
        }
      >
        <span className="relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/25 backdrop-blur-sm">
          <HeartHandshake size={15} strokeWidth={2.25} />
          <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
          </span>
        </span>
        <span className="flex-1 leading-tight drop-shadow-sm">Gestion Sociale</span>
        <span className="rounded-full bg-white px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-teal-700 shadow-sm">
          Pro
        </span>
      </NavLink>
    </div>
  );
}

export default function TitulaireLayout() {
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  // Sur un poste comptoir, cette interface complète est une PARENTHÈSE : le
  // titulaire vient de l'ouvrir avec son PIN, et elle se referme seule s'il
  // s'éloigne. Ailleurs, ce crochet ne fait rien.
  const { onCounterPost, remainingMs } = useCounterIdleLock();
  const lockCounter = useLockCounter();
  const minutesLeft = Math.ceil(remainingMs / 60000);
  // LOT_ROLES_HUB : l'équipe PharmaGestion (le boss est aussi titulaire) garde un
  // accès à la console depuis n'importe quelle officine.
  const { data: plateforme } = usePlateforme();

  return (
    <div className="w-full h-screen bg-slate-100 flex overflow-hidden">
      <aside className="w-60 flex flex-col" style={{ backgroundColor: '#0f1f35' }}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
              <Pill size={17} color="#fff" strokeWidth={2.25} />
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight text-white">Officina</div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 -mt-0.5">RH Pharmacie</div>
            </div>
          </div>
        </div>

        {/* Option payante : mise en avant, au-dessus de tout le reste */}
        {user?.options?.gestionSociale && <LienGestionSociale />}

        {/* Nav */}
        <nav className="flex-1 px-2.5 py-4 space-y-0.5 overflow-y-auto">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors ${
                    isActive
                      ? 'bg-white/10 text-white font-medium'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`
                }
              >
                <Icon size={15} strokeWidth={1.75} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {estEquipe(plateforme?.role) && (
          <div className="px-2.5 pb-2">
            <NavLink
              to="/console"
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-amber-300 hover:bg-white/5"
            >
              <ShieldCheck size={15} strokeWidth={1.75} />
              <span>Console PharmaGestion</span>
            </NavLink>
          </div>
        )}

        {/* Pharmacie active (multi-site) — tout en bas, sous la navigation */}
        <SiteSwitcher />

        {/* Poste comptoir : la sortie, toujours à portée de main */}
        {onCounterPost && (
          <div className="px-2.5 pt-3">
            <button
              onClick={lockCounter}
              title="Referme la gestion et rend l'écran à l'équipe"
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-accent/20 hover:bg-accent/30 text-left"
            >
              <Lock size={15} strokeWidth={1.75} className="text-white/70 flex-shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-[9px] uppercase tracking-[0.12em] text-white/40">
                  Poste comptoir
                </span>
                <span className="block text-[12.5px] text-white truncate">
                  Verrouiller l'écran
                </span>
              </span>
              <span className="text-[10px] text-white/40 tabular-nums flex-shrink-0">
                {minutesLeft > 0 ? `${minutesLeft} min` : ''}
              </span>
            </button>
          </div>
        )}

        {/* User */}
        <div className="px-4 py-3 border-t border-white/10 mt-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-accent text-white flex items-center justify-center text-[11px] font-semibold">
              {user?.email?.[0].toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-white truncate">
                {user?.email ?? 'Titulaire'}
              </div>
              <div className="text-[10px] text-white/50">
                {user?.pharmacy?.name}
              </div>
            </div>
            <button
              onClick={logout}
              title="Se déconnecter"
              className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 bg-white overflow-hidden flex flex-col">
        <PlatformBanner />
        <Outlet />
      </main>
    </div>
  );
}
